import { useState } from 'react'
import { Screen, SectionHeader } from '@/components/layout'
import { SegmentedControl } from '@/components/primitives'
import { MetricRow, type MetricSpec } from '@/components/cards'
import { LineChart } from '@/components/charts/LineChart'
import { List, MilestoneRow } from '@/components/rows'
import {
  EmptyState,
  ErrorState,
  InsufficientData,
  LoadingRegion,
  SkeletonCard,
  SkeletonList,
  SkeletonMetrics,
} from '@/components/states'
import { GeneratedText } from '@/components/GeneratedText'
import type { Progress } from '@/generated/types'
import { api } from '@/lib/api'
import { useProseState } from '@/lib/analysis'
import { t } from '@/lib/copy'
import { formatMonthShort, formatMoney } from '@/lib/format'
import { useNavigate } from '@/lib/router'
import { useAsync } from '@/lib/useAsync'

const RANGES: { value: Progress['range']; labelKey: 'progress.range.3m' | 'progress.range.6m' | 'progress.range.12m' | 'progress.range.all' }[] = [
  { value: '3m', labelKey: 'progress.range.3m' },
  { value: '6m', labelKey: 'progress.range.6m' },
  { value: '12m', labelKey: 'progress.range.12m' },
  { value: 'all', labelKey: 'progress.range.all' },
]

/** Score and key metrics over time, plus the recommendations acted on. */
export function ProgressScreen() {
  const navigate = useNavigate()
  const [range, setRange] = useState<Progress['range']>('12m')
  const progress = useAsync(() => api.progress(range), [range])

  const data = progress.state.data
  const points = data?.points ?? []

  const prose = useProseState(data?.narrativeState, progress.reload)

  const dataState =
    progress.state.status === 'error'
      ? 'error'
      : progress.state.status === 'loading' && !data
        ? 'loading'
        : points.length === 0 && (data?.milestones.length ?? 0) === 0
          ? 'empty'
          : points.length < 2
            ? 'insufficientData'
            : 'ready'

  // The chart, the metrics and the milestones do not depend on the narrative,
  // so `analysisPending` and `analysisFailed` only ever refine `ready` — and
  // both render the identical tree. Milestones stay keyed in all of them.
  const screenState = dataState === 'ready' ? (prose.state ?? 'ready') : dataState

  // The three states that render the identical tree below. `analysisPending`
  // adds no spinner and disables nothing; `analysisFailed` is not the error
  // treatment. The only difference between them and `ready` is whether the
  // narrative slot holds prose or its copyKey — and it holds one either way.
  const rendersContent =
    screenState === 'ready' || screenState === 'analysisPending' || screenState === 'analysisFailed'

  const currency = data?.savedToDate?.currency ?? 'USD'
  const metrics: MetricSpec[] = [
    {
      key: 'change',
      label: t('progress.metric.scoreChange'),
      value:
        data?.scoreChange !== null && data?.scoreChange !== undefined
          ? `${data.scoreChange > 0 ? '+' : ''}${data.scoreChange}`
          : '—',
      tone: (data?.scoreChange ?? 0) >= 0 ? 'positive' : 'negative',
    },
    {
      key: 'saved',
      label: t('progress.metric.saved'),
      value: formatMoney(data?.savedToDate ?? { amount: 0, currency }, { absolute: true }),
    },
    {
      key: 'actions',
      label: t('progress.section.milestones'),
      value: t('progress.metric.actionsTaken', { count: data?.actionsTaken ?? 0 }),
    },
  ]

  return (
    <Screen
      id="progress"
      state={screenState}
      actions={
        <SegmentedControl
          options={RANGES.map((r) => ({ value: r.value, label: t(r.labelKey) }))}
          value={range}
          onChange={setRange}
          label={t('progress.title')}
        />
      }
    >
      {screenState === 'loading' ? (
        <LoadingRegion>
          <SkeletonCard height="var(--space-xxl)" />
          <SkeletonMetrics count={3} />
          <SkeletonList rows={4} />
        </LoadingRegion>
      ) : screenState === 'error' ? (
        <ErrorState
          titleKey="progress.error.title"
          error={progress.state.status === 'error' ? progress.state.error : undefined}
          onRetry={progress.reload}
        />
      ) : screenState === 'empty' ? (
        <EmptyState titleKey="progress.empty.title" />
      ) : screenState === 'insufficientData' ? (
        <InsufficientData titleKey="progress.insufficientData.title" />
      ) : rendersContent ? (
        <div className="stack">
          {/* Above the chart, per progress.schema.json — the one thing on this
              screen a chart cannot say. Keyed until prose arrives. */}
          <GeneratedText
            text={data?.narrative?.summary}
            copyKey="progress.summary.fallback"
            params={{ months: points.length }}
            className="body-secondary"
          />

          <div className="card card-pad-lg">
            <LineChart
              points={points.map((point) => ({ label: formatMonthShort(point.month), value: point.score }))}
              caption={t('dashboard.score.label')}
              formatValue={(value) => String(Math.round(value))}
            />
          </div>

          <MetricRow metrics={metrics} />

          <section>
            <SectionHeader title={t('progress.section.milestones')} />
            <List>
              {(data?.milestones ?? []).map((milestone) => (
                <MilestoneRow
                  key={milestone.id}
                  milestone={milestone}
                  onSelect={
                    milestone.linkScreen === 'recommendationDetail' && milestone.linkId
                      ? () => navigate('recommendationDetail', { id: milestone.linkId! })
                      : milestone.linkScreen === 'healthScore'
                        ? () => navigate('healthScore')
                        : undefined
                  }
                />
              ))}
            </List>
          </section>
        </div>
      ) : null}
    </Screen>
  )
}
